import { Component, ViewChild } from '@angular/core';
import { ModalService } from '@app/core/services/modal.service';
import {
  AggregatedMeteredDataQueryResultDto,
  BusinessReasonCode,
  DataHubStatus,
  MeteringPointType,
  ProcessVariant,
  SettlementMethod,
} from '@app/api/dmc';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { CardComponent } from '@shared/components/card/card.component';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { TableComponent } from '@shared/components/table/table.component';
import { HeaderService } from '@app/core/services/header.service';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { settlementMeteredTable } from '@shared/table-configs/settlement/settlement-metered.table';
import {
  SettlementMeteredRequestModalComponent
} from '@shared/modals/settlement/settlement-metered-request-modal/settlement-metered-request-modal.component';
import { settlementActionMenu } from '@views/settlement/settlement.action-menu';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import {
  SettlementMeteredViewModalComponent
} from '@shared/modals/settlement/settlement-metered-view-modal/settlement-metered-view-modal.component';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import { sortByTranslation } from '@app/core/utils/sort-by-translation';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { SortParam } from '@app/core/constants/sort-param';
import { ODataService } from '@app/core/services/odata/odata.service';
import { ODataType } from '@app/core/services/odata/types/odata-type';

@Component({
  selector: 'app-settlement-aggregated-metereddata',
  imports: [
    CardComponent,
    ButtonDirective,
    TranslatePipe,
    InputGroupComponent,
    LabelDirective,
    InputDirective,
    FormsModule,
    ReactiveFormsModule,
    TableComponent,
    SelectDirective
  ],
  templateUrl: './settlement-aggregated-metereddata.component.html',
  styleUrl: './settlement-aggregated-metereddata.component.css'
})
export class SettlementAggregatedMetereddataComponent {
  @ViewChild('table', { static: false }) public table?: TableComponent<any>;

  public searchForm: FormGroup = new FormGroup({
    startDate: new FormControl(),
    endDate: new FormControl(),
    typeOfMeteringPoint: new FormControl(),
    settlementMethod: new FormControl(),
    businessReasonCode: new FormControl(),
    processVariant: new FormControl(),
    dataHubStatus: new FormControl(),
  });

  public actions: MenuAction[] = [
    {
      ...settlementActionMenu[0],
      callback: () => this.openViewModal(),
      disabled: () => this.selectedElements.length !== 1,
    },
    {
      ...settlementActionMenu[1],
      callback: () => this.openRequestModal(),
    }
  ]

  public selectedElements: AggregatedMeteredDataQueryResultDto[] = [];

  /** Table **/
  public tableConfig: TableConfig<AggregatedMeteredDataQueryResultDto> = {
    ...settlementMeteredTable,
    oData: true,
    pageSizeSelection: true,
    serviceCallback: (pageSize: number, page: number, order?: SortParam) => this.searchCallback(pageSize, page, order),
    contextActions: [
      this.actions[0],
    ]
  };

  /** Lister **/
  public meteringPointType: KeyValue[] = sortByTranslation(enumToKeyValue(MeteringPointType, { prefixValue: 'enum.meteringpoint.meteringpointtype.', omitKeys: ['NotSpecified'] }));
  public settlementMethod: KeyValue[] = enumToKeyValue(SettlementMethod, { prefixValue: 'enum.meteringpoint.settlementmethod.', omitKeys: ['NotSpecified'] });
  public businessReasonCode: KeyValue[] = enumToKeyValue(BusinessReasonCode, { prefixValue: 'enum.settlement.businessreasoncode.', includeKeys: ['Temporary', 'FirstSettlement', 'SecondSettlement', 'CorrectionSettlement'] });
  public processVariant: KeyValue[] = enumToKeyValue(ProcessVariant, { prefixValue: 'enum.settlement.processvariant.', omitKeys: ['NotSpecified', 'FirstSettlement', 'SecondSettlement'] });
  public dataHubStatus: KeyValue[] = enumToKeyValue(DataHubStatus, { prefixValue: 'enum.datahub.status.', omitKeys: ['NotSpecified', 'NotApplicable'] });


  constructor(
    private readonly modalService: ModalService,
    private readonly headerMenuService: HeaderService,
    private readonly oDataService: ODataService,
  ) {
    this.headerMenuService.setActions(this.actions);
  }

  public searchCallback(pageSize: number, page: number, sort?: SortParam) {
    const data = this.searchForm.value;

    const query = this.oDataService.dmc.query()
      .paginate(page, pageSize)
      .count();

    if (data.startDate)           query.param('StartDate',           data.startDate);
    if (data.endDate)             query.param('EndDate',             data.endDate);
    if (data.typeOfMeteringPoint) query.param('TypeOfMeteringPoint', data.typeOfMeteringPoint);
    if (data.settlementMethod)    query.param('SettlementMethod',    data.settlementMethod);
    if (data.businessReasonCode)  query.param('BusinessReasonCode',  data.businessReasonCode);
    if (data.processVariant)      query.param('ProcessVariant',      data.processVariant);
    if (data.dataHubStatus)       query.param('DataHubStatus',       data.dataHubStatus);

    if (sort) {
      query.orderBy(`${sort.sortField} ${sort.sortDirection}`);
    }

    return this.oDataService.dmc.getCollection<AggregatedMeteredDataQueryResultDto>(
      ODataType.queryAggregatedMeteredData,
      query,
    );
  }

  public openViewModal(): void {
    this.modalService.show({
      component: SettlementMeteredViewModalComponent,
      title: 'settlement.metered.view.title',
      actions: [
        { type: 'secondary', label: 'system.button.close', closeOnClick: true },
      ],
      params: {
        id: this.selectedElements[0].aggregatedMeteredDataId,
      }
    })
  }

  public openRequestModal(): void {
    this.modalService.show({
      component: SettlementMeteredRequestModalComponent,
      title: 'settlement.metered.request.title',
      actions: [
        { type: 'primary', label: 'system.button.request', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
    })
  }

}
